import { Flex } from "@/components/atoms/flex";
import { useInteractionMenuStyle } from "@/components/molecules/interactionMenu/style";
import {
	Button,
	Divider,
	MenuCheckedValueChangeData,
	MenuDivider,
	MenuGroupHeader,
	MenuItemCheckbox,
	MenuList,
	mergeClasses,
} from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import {
	interactionHistoryTypeFilters,
	HistoryInteractionHandlingFilters,
	interactionHistoryHandlingFilters,
	interactionHistoryMiscellaneousFilters,
	HistoryInteractionMiscellaneousFilters,
	InteractionHistoryFilter,
} from "./types";
import {
	historyInteractionsHandlingFiltersIconMap,
	historyInteractionsIconMapMedium,
	historyInteractionsMiscelleanousFiltersIconMap,
} from "./helpers";
import { HistoryInteractionTypes } from "@/repository/models/history";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useHistoryFilterStore } from "@/common/stores/nonpersistingStores/history/historyFilterStore";
import {
	HistoryFilterActions,
	HistoryFilterState,
} from "@/common/stores/nonpersistingStores/history/types";
import { Person20Regular } from "@fluentui/react-icons";
import { useQueueSupervisorOperator } from "@/hooks/supervisor";

export const InteractionHistoryFiltersPopoverView = () => {
	const intl = useIntl();

	const cacheKey: CacheKey = CacheKey.HistoryFilter;

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const historyFilterActions = useHistoryFilterStore(
		(actions: HistoryFilterActions) => actions
	);

	const historyFilterState = useHistoryFilterStore(
		(state: HistoryFilterState) => state
	);

	const isSupervisorOrOperator = useQueueSupervisorOperator();

	const [interactionHistoryTypes, setInteractionHistoryTypes] = useState<
		HistoryInteractionTypes[]
	>(
		useCallback(
			() => appStoreCache[cacheKey]?.types || ([] as HistoryInteractionTypes[]),
			[appStoreCache]
		)
	);

	const [interactionHistoryHandling, setInteractionHistoryHandling] = useState<
		HistoryInteractionHandlingFilters[]
	>(
		useCallback(
			() =>
				(appStoreCache[cacheKey]?.handling ||
					[]) as HistoryInteractionHandlingFilters[],
			[appStoreCache]
		)
	);

	const [interactionHistoryMiscellaneous, setInteractionHistoryMiscellaneous] =
		useState<HistoryInteractionMiscellaneousFilters[]>(
			useCallback(
				() =>
					(appStoreCache[cacheKey]?.miscellaneous ||
						[]) as HistoryInteractionMiscellaneousFilters[],
				[appStoreCache]
			)
		);

	const [showMyInteractions, setShowMyInteractions] = useState<boolean>(
		useCallback(
			() => appStoreCache[cacheKey]?.showMyInteractions || false,
			[appStoreCache]
		)
	);

	const menuClasses = useInteractionMenuStyle();

	const classes = useStyle();

	const typeFilterKey = "typeFilterKey";

	const handlingFilterKey = "handlingFilterKey";

	const miscellaneousFilterKey = "miscellaneousFilterKey";

	const myInteractionsFilterKey = "myInteractionsFilterKey";

	useEffect(() => {
		const currentFilter = historyFilterState.filterData;

		const effectiveShowMyInteractions = isSupervisorOrOperator
			? showMyInteractions
			: false;

		if (
			interactionHistoryTypes.length !== currentFilter?.types?.length ||
			!interactionHistoryTypes.every((h) =>
				currentFilter?.types?.includes(h)
			) ||
			interactionHistoryHandling.length !== currentFilter?.handling?.length ||
			!interactionHistoryHandling.every((h) =>
				currentFilter?.handling?.includes(h)
			) ||
			interactionHistoryMiscellaneous.length !==
				currentFilter?.miscellaneous?.length ||
			!interactionHistoryMiscellaneous.every((h) =>
				currentFilter?.miscellaneous?.includes(h)
			) ||
			effectiveShowMyInteractions !== currentFilter?.showMyInteractions
		) {
			const newFilter = {
				types: interactionHistoryTypes,
				handling: interactionHistoryHandling,
				miscellaneous: interactionHistoryMiscellaneous,
				showMyInteractions: effectiveShowMyInteractions,
			} as InteractionHistoryFilter;

			appStoreActions.setCache(cacheKey, newFilter);
			historyFilterActions.setFilterData(newFilter);
		}
	}, [
		interactionHistoryTypes,
		interactionHistoryHandling,
		interactionHistoryMiscellaneous,
		showMyInteractions,
		historyFilterState.filterData,
		isSupervisorOrOperator,
	]);

	useEffect(() => {
		if (!isSupervisorOrOperator && showMyInteractions) {
			setShowMyInteractions(false);
		}
	}, [isSupervisorOrOperator]);

	const cleanFilters = useCallback(() => {
		setInteractionHistoryTypes([]);
		setInteractionHistoryHandling([]);
		setInteractionHistoryMiscellaneous([]);
		setShowMyInteractions(false);
	}, [
		setInteractionHistoryTypes,
		setInteractionHistoryHandling,
		setInteractionHistoryMiscellaneous,
		setShowMyInteractions,
	]);

	const menuGroupHeaderItem = useCallback(
		(filterMessage: string) => [
			<MenuGroupHeader
				key="interactionHeader"
				className={menuClasses.filterGroupHeader}
			>
				{filterMessage}
				<Button
					appearance="transparent"
					className={menuClasses.cleanButtonSize}
					onClick={() => {
						cleanFilters();
					}}
				>
					{intl.formatMessage({ id: "InteractionMenu.Reset" })}
				</Button>
			</MenuGroupHeader>,
			<MenuDivider key="interactionDivider" />,
		],
		[]
	);

	const interactionHistoryTypeFilterName = useCallback(
		(typeFilter: HistoryInteractionTypes) =>
			intl.formatMessage({
				id: `History.Filters.${HistoryInteractionTypes[typeFilter]}`,
			}),
		[intl]
	);

	const interactionHistoryHandlingFilterName = useCallback(
		(handlingFilter: HistoryInteractionHandlingFilters) =>
			intl.formatMessage({
				id: `History.Filters.${HistoryInteractionHandlingFilters[handlingFilter]}`,
			}),
		[intl]
	);

	const interactionHistoryMiscellaneousFilterName = useCallback(
		(handlingFilter: HistoryInteractionMiscellaneousFilters) =>
			intl.formatMessage({
				id: `History.Filters.${HistoryInteractionMiscellaneousFilters[handlingFilter]}`,
			}),
		[intl]
	);

	const interactionTypeFilterValues = useMemo<JSX.Element[]>(
		() =>
			interactionHistoryTypeFilters.map((type: HistoryInteractionTypes) => (
				<MenuItemCheckbox
					key={type}
					name={typeFilterKey}
					icon={historyInteractionsIconMapMedium[type]}
					value={type.toString()}
					className={mergeClasses(
						menuClasses.filterCheckboxItem,
						classes.filterElement
					)}
				>
					{interactionHistoryTypeFilterName(type)}
				</MenuItemCheckbox>
			)),
		[interactionHistoryTypeFilters]
	);

	const myInteractionsFilter = useMemo<JSX.Element>(
		() => (
			<MenuItemCheckbox
				key={myInteractionsFilterKey}
				name={myInteractionsFilterKey}
				icon={<Person20Regular />}
				value={"myInteractions"}
				className={mergeClasses(
					menuClasses.filterCheckboxItem,
					classes.filterElement
				)}
			>
				{intl.formatMessage({
					id: `History.Filters.MyInteractions`,
				})}
			</MenuItemCheckbox>
		),
		[]
	);

	const interactionHandlingFilters = useMemo<JSX.Element[]>(
		() =>
			interactionHistoryHandlingFilters.map(
				(handlingFilter: HistoryInteractionHandlingFilters) => (
					<MenuItemCheckbox
						key={handlingFilter}
						name={handlingFilterKey}
						icon={historyInteractionsHandlingFiltersIconMap[handlingFilter]}
						value={handlingFilter.toString()}
						className={mergeClasses(
							menuClasses.filterCheckboxItem,
							classes.filterElement
						)}
					>
						{interactionHistoryHandlingFilterName(handlingFilter)}
					</MenuItemCheckbox>
				)
			),
		[interactionHistoryHandlingFilters]
	);

	const interactionMiscellaneousFilters = useMemo<JSX.Element[]>(
		() =>
			interactionHistoryMiscellaneousFilters.map(
				(miscellaneousFilter: HistoryInteractionMiscellaneousFilters) => (
					<MenuItemCheckbox
						key={miscellaneousFilter}
						name={miscellaneousFilterKey}
						icon={
							historyInteractionsMiscelleanousFiltersIconMap[
								miscellaneousFilter
							]
						}
						value={miscellaneousFilter.toString()}
						className={mergeClasses(
							menuClasses.filterCheckboxItem,
							classes.filterElement
						)}
					>
						{interactionHistoryMiscellaneousFilterName(miscellaneousFilter)}
					</MenuItemCheckbox>
				)
			),
		[interactionHistoryMiscellaneousFilters]
	);

	const onInteractionTypesChanged = useCallback(
		(_: any, data: MenuCheckedValueChangeData) => {
			setInteractionHistoryTypes(
				data.checkedItems.map((ci) => Number(ci) as HistoryInteractionTypes)
			);
		},
		[setInteractionHistoryTypes]
	);

	const onInteractionHandlingFiltersChanged = useCallback(
		(_: any, data: MenuCheckedValueChangeData) => {
			setInteractionHistoryHandling(
				data.checkedItems.map(
					(ci) => Number(ci) as HistoryInteractionHandlingFilters
				)
			);
		},
		[setInteractionHistoryHandling]
	);

	const onInteractionMiscellaneousFiltersChanged = useCallback(
		(_: any, data: MenuCheckedValueChangeData) => {
			setInteractionHistoryMiscellaneous(
				data.checkedItems.map(
					(ci) => Number(ci) as HistoryInteractionMiscellaneousFilters
				)
			);
		},
		[setInteractionHistoryMiscellaneous]
	);

	const onMyInteractionsChanged = useCallback(
		(_: any, data: MenuCheckedValueChangeData) => {
			setShowMyInteractions(data.checkedItems.includes("myInteractions"));
		},
		[setShowMyInteractions]
	);

	return (
		<Flex column>
			{menuGroupHeaderItem(
				intl.formatMessage({ id: "InteractionMenu.ShowOnly" })
			)}
			{isSupervisorOrOperator && (
				<>
					<MenuList
						checkedValues={{
							[myInteractionsFilterKey]: showMyInteractions
								? ["myInteractions"]
								: [],
						}}
						onCheckedValueChange={onMyInteractionsChanged}
						className={classes.generalMenuSection}
					>
						{myInteractionsFilter}
					</MenuList>
					<Divider />
				</>
			)}
			<MenuList
				checkedValues={{
					[miscellaneousFilterKey]: interactionHistoryMiscellaneous.map((ihm) =>
						ihm.toString()
					),
				}}
				onCheckedValueChange={onInteractionMiscellaneousFiltersChanged}
				className={classes.generalMenuSection}
			>
				{interactionMiscellaneousFilters}
			</MenuList>
			<Divider />
			<MenuList
				checkedValues={{
					[typeFilterKey]: interactionHistoryTypes.map((iht) => iht.toString()),
				}}
				onCheckedValueChange={onInteractionTypesChanged}
				className={classes.generalMenuSection}
			>
				{interactionTypeFilterValues}
			</MenuList>
			<Divider />
			<MenuList
				className={classes.generalMenuSection}
				checkedValues={{
					[handlingFilterKey]: interactionHistoryHandling.map((ihhf) =>
						ihhf.toString()
					),
				}}
				onCheckedValueChange={onInteractionHandlingFiltersChanged}
			>
				{interactionHandlingFilters}
			</MenuList>
		</Flex>
	);
};
